import { Dashboard, PlusSquare, QuestionCircle } from "@reverb-ui/icons-react";
import {
  VStack,
  Box,
  Button,
  Center,
  Col,
  Row,
  Skeleton,
  SkeletonCircle,
  Text,
  Tooltip,
} from "@reverb-ui/react";
import { InsightsFeedbackEnum } from "data/constants/constants";
import { cardsInfo } from "helper/cardsHelper";
import { panelsInfo } from "helper/panelsHelper";
import { MetricCard } from "presentation/components/MetricCard";
import { CardSkeleton } from "presentation/components/Skeleton/CardSkeleton";
import { ListTagRowSkeleton } from "presentation/components/Skeleton/TagRowSkeleton";
import { useContext, useMemo } from "react";
import { DropoffCalculatorContext } from "../context-api/DropoffCalculatorProvider";
import TagRow from "./TagRow";
import PanelContainer from "presentation/components/PanelContainer";

const dropoffCalculatorInfo = panelsInfo.dropoffCalculator;

export const DropoffCalculator = () => {
  const {
    requestParamsIndex,
    range,
    dropOffItems,
    dropOffSelectOptions,
    setSelectedItemByIndex,
    removeItem,
    dropOffState,
    maxTagsNumber,
    addNewItem,
    downloadingReports,
    downloadDropOffCSV,
    isLoading,
  } = useContext(DropoffCalculatorContext);

  const canAddNewTag = useMemo(() => {
    return dropOffState.length < maxTagsNumber;
  }, [dropOffState, maxTagsNumber]);

  const averageDropOff = useMemo(() => {
    const filledItems = dropOffItems.filter((item) => !!item);
    const sum = filledItems.reduce(
      (acc, item) => acc + (Number(item?.dropOffPercentage) || 0),
      0
    );

    return sum / (filledItems.length || 1);
  }, [dropOffItems]);

  const showCriticalDropOff = useMemo(() => {
    return averageDropOff >= 20;
  }, [averageDropOff]);

  const averageDropoffInsightMessage = useMemo(() => {
    if (!averageDropOff) return;

    if (averageDropOff <= 4) {
      return {
        feedback: InsightsFeedbackEnum.POSITIVE,
        message:
          "Congratulations, your content performed very well. Use it as a reference to create new experiences",
      };
    } else {
      return {
        feedback: InsightsFeedbackEnum.NEGATIVE,
        message:
          "Your experience could have performed better. Dedicate time to review elements such as design, content, and operations",
      };
    }
  }, [averageDropOff]);

  return (
    <PanelContainer
      panelId={dropoffCalculatorInfo.htmlId}
      panelName={dropoffCalculatorInfo.description}
      rangeName={range}
      isLoading={isLoading}
      downloadOptions={{
        onClickGenerateCSV: downloadDropOffCSV,
        downloadingReports: downloadingReports,
        panelInfo: dropoffCalculatorInfo,
        filterStateIndex: requestParamsIndex,
      }}
    >
      <Row>
        <Col col={9} h="100%">
          <Box bg="white" w="full" h="100%" p={6}>
            <Row>
              <Col col={7}>
                <SkeletonCircle
                  isLoaded={!isLoading}
                  variant="rect"
                  width={4}
                  height={4}
                >
                  <Dashboard color="gray.500" />
                </SkeletonCircle>
                <Skeleton
                  isLoaded={!isLoading}
                  variant="rect"
                  marginLeft="9px"
                  marginRight="9px"
                >
                  <Text
                    color="gray.500"
                    textTransform="uppercase"
                    fontSize="xs"
                    fontWeight="semibold"
                  >
                    Tag flow
                  </Text>
                </Skeleton>
                <SkeletonCircle
                  isLoaded={!isLoading}
                  variant="rect"
                  width={4}
                  height={4}
                >
                  <Tooltip
                    label="Assign a tag to each flow step"
                    placement="bottom"
                    bg="gray.100"
                    color="gray.800"
                    shouldWrapChildren
                  >
                    <QuestionCircle color="gray.400" />
                  </Tooltip>
                </SkeletonCircle>
              </Col>
              <Col col={2} justifyContent="center">
                <Center>
                  <Skeleton isLoaded={!isLoading} variant="rect">
                    <Text
                      color="gray.500"
                      textTransform="uppercase"
                      fontSize="xs"
                      fontWeight="semibold"
                    >
                      Unique visitors
                    </Text>
                  </Skeleton>
                </Center>
              </Col>
              <Col col={2} justifyContent="center">
                <Center>
                  <Skeleton isLoaded={!isLoading} variant="rect">
                    <Text
                      color="gray.500"
                      textTransform="uppercase"
                      fontSize="xs"
                      fontWeight="semibold"
                    >
                      Drop-off
                    </Text>
                  </Skeleton>
                </Center>
              </Col>
            </Row>
            <Box h={405} overflowY="auto" w="100%" overflowX="hidden">
              {isLoading ? (
                <ListTagRowSkeleton numberOfTags={5} />
              ) : (
                dropOffItems.map((item, index) => (
                  <TagRow
                    onChange={(option) => setSelectedItemByIndex(option, index)}
                    position={index}
                    key={index}
                    dropOffItem={item}
                    onClickRemove={() => removeItem(index)}
                    dropOffSelectOptions={dropOffSelectOptions}
                    canRemove={dropOffState.length > 2}
                  />
                ))
              )}
            </Box>
            <Skeleton isLoaded={!isLoading} variant="rect" width={"12%"}>
              {canAddNewTag ? (
                <Button
                  color="blue.500"
                  variant="ghost"
                  onClick={addNewItem}
                  leftIcon={<PlusSquare color="blue.500" />}
                  size="sm"
                >
                  New step
                </Button>
              ) : (
                <Tooltip
                  label="You can't add a new step"
                  placement="bottom"
                  bg="gray.100"
                  color="gray.800"
                  shouldWrapChildren
                >
                  <Button
                    color="blue.500"
                    variant="ghost"
                    disabled
                    leftIcon={<PlusSquare color="blue.500" />}
                    size="sm"
                  >
                    New step
                  </Button>
                </Tooltip>
              )}
            </Skeleton>
          </Box>
        </Col>
        <Col col={3}>
          <VStack w="full" spacing={5}>
            {isLoading ? (
              <CardSkeleton hasRecomendation={true} />
            ) : (
              <>
                {showCriticalDropOff && (
                  <MetricCard
                    order={
                      cardsInfo.cards.find((_) => _.name === "criticalDropoff")
                        ?.cardId!
                    }
                    iconName="Dashboard"
                    title="Critical drop-off"
                    description={`Aprox ${averageDropOff.toFixed(
                      1
                    )}% of visitors`}
                    subDescription="exited the flow before finishing it"
                    isLoading={false}
                    insight={{
                      feedback: InsightsFeedbackEnum.NEGATIVE,
                      message:
                        "Your users are leaving the experience at a certain point. Review your experience to improve results.",
                    }}
                  />
                )}
                <MetricCard
                  order={
                    cardsInfo.cards.find((_) => _.name === "averageDropoff")
                      ?.cardId!
                  }
                  iconName="Dashboard"
                  title="Average drop-off"
                  upDescription="The average dropoff for the whole flow is aproximately"
                  description={`${averageDropOff.toFixed(1) || 0}% `}
                  insight={averageDropoffInsightMessage}
                  isLoading={false}
                />
              </>
            )}
          </VStack>
        </Col>
      </Row>
    </PanelContainer>
  );
};
